import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, Loader2, RefreshCw, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const TAG_STYLES = {
  Awards: "bg-amber-100 text-amber-800 border-amber-200",
  LinkedIn: "bg-sky-100 text-sky-800 border-sky-200",
  "Case study": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "PR / Media": "bg-purple-100 text-purple-800 border-purple-200",
  "Bid evidence": "bg-gray-100 text-gray-700 border-gray-200",
};

export const SmartSuggestions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (generate = true) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/smart-suggestions", { params: { generate } });
      setData(data);
    } catch {
      setData({ suggestions: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const suggestions = data?.suggestions || [];
  if (!loading && suggestions.length === 0) return null;

  return (
    <div className="rcw-card mt-6 p-4 sm:p-5 border-sky-200 bg-gradient-to-r from-sky-50/70 to-white" data-testid="smart-suggestions-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-sky-600" />
          <div className="text-xs uppercase tracking-widest font-bold text-sky-700">Smart suggestions</div>
        </div>
        <button onClick={() => load(true)} disabled={loading} className="text-gray-400 hover:text-sky-600 transition" data-testid="smart-suggestions-refresh" title="Refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>
      {loading && suggestions.length === 0 ? (
        <div className="text-sm text-gray-400 py-2">Scanning recent submissions…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {suggestions.slice(0, 6).map((s) => (
            <Link
              key={s.project_id}
              to={`/admin/projects/${s.project_id}`}
              className="group flex flex-col gap-1.5 rounded-xl border border-gray-200 bg-white p-3.5 hover:border-sky-300 hover:shadow-sm transition"
              data-testid={`smart-suggestion-${s.project_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-900 truncate">{s.title}</span>
                <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-sky-500 shrink-0 transition" />
              </div>
              <div className="space-y-1">
                {(s.items || []).slice(0, 2).map((it, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${TAG_STYLES[it.tag] || TAG_STYLES["Bid evidence"]}`}>
                      {it.tag}
                    </span>
                    <span className="text-xs text-gray-600 leading-snug">{it.text}</span>
                  </div>
                ))}
              </div>
            </Link>
          ))}
        </div>
      )}
      {data?.budget_reached && (
        <div className="mt-3 text-xs text-amber-700" data-testid="smart-suggestions-budget-note">
          Monthly AI budget reached — showing cached suggestions only.
        </div>
      )}
    </div>
  );
};
